/**
 * Theme type definitions for the dashboard theming system.
 *
 * The Theme type can contain non-JSON-serializable attributes (e.g., functions).
 * Only `themeId` is persisted to the backend.
 */

import type * as AgGridGlobals from "ag-grid-community";

export interface Theme {
  themeId: string;
  themeName: string;

  // Logo variant:
  // - "dark" = dark logo and text (for light backgrounds)
  // - "light_text" = dark logo with white text (for dark backgrounds)
  // - "light_logo_and_text" = white logo and text (for colored/dark nav backgrounds)
  logoVariant: "dark" | "light_text" | "light_logo_and_text";

  // Semantic TEXT tokens for KPI delta text (`.metric-status-*` classes). When set, applyTheme
  // publishes these as the `--coeff-*` CSS vars; charts keep using the chartjs tokens (the design
  // pins different values for text vs charts, e.g. the universal 40%-grey chart negative would be
  // unreadable as text). Legacy themes omit this and fall back to the chartjs tokens.
  semanticText?: { positive: string; negative: string; neutral: string };

  // Custom brand logo (optional) - replaces Coefficient logo when set
  // When customLogo is provided, "Powered by Coefficient" text is shown next to it
  customLogo?: {
    src: string; // URL or data URI of the logo image
    alt: string; // Alt text for accessibility
    height?: number; // Optional height in pixels (default: 32)
  };

  // Tailwind CSS theming - CSS custom properties
  tailwind: TailwindThemeConfig;

  // Chart.js theming - applied via Chart.defaults
  chartjs: ChartJSThemeConfig;

  // AG Grid theming - theme object with params
  agGrid: AGGridThemeConfig;

  // Optional extra CSS to inject for this theme (e.g., animations, custom styles)
  extraCss?: string;

  // Optional extra HTML to inject for this theme (e.g., decorative elements)
  // Injected as a container div at the start of the body
  extraHtml?: string;

  // Optional setup function for theme-specific behavior (e.g., scroll listeners, animations)
  // Returns an optional cleanup function that is called when switching away from the theme
  setup?: ThemeSetupFn;
}

/** Cleanup function returned by theme setup (called when switching away from theme) */
export type ThemeCleanupFn = () => void;

/** Function type for theme-specific setup logic (runs in iframe context) */
export type ThemeSetupFn = (doc: Document, win: Window) => ThemeCleanupFn | void;

/**
 * Configuration for text buttons within a UI surface.
 */
export interface TextButtonConfig {
  hoverBackgroundColor: string;
  hoverForegroundColor: string;
  borderRadius?: string; // Optional, defaults to general.borderRadius
}

/**
 * Configuration for a UI surface (main, nav, card).
 * Each surface has consistent styling properties for backgrounds, text, borders, and hover states.
 */
export interface UISurfaceConfig {
  backgroundColor: string;
  // Optional full `background` override for the dashboard-card surface only (e.g. the Figma card
  // gradient that lets the page background bleed through). Other bg-card-background consumers
  // (panels, inputs) keep the solid backgroundColor.
  cardSurfaceBackground?: string;
  // Optional secondary/inset background for subtle panels layered on top of this surface
  // (e.g. the sections inside the Subscribe modal). Defaults to the surface's text-button
  // hover background, which every theme already tunes as a subtle surface.
  secondaryBackgroundColor?: string;
  // Optional tertiary background, one step deeper than secondary — for controls inset within a
  // secondary panel (e.g. the segmented-control track in the Subscribe modal). Defaults to the
  // surface's text-button hover background.
  tertiaryBackgroundColor?: string;
  // Optional fill for removable tags/chips (e.g. recipients in the Subscribe modal). Defaults
  // to a 15% wash of the primary button color over this surface's background.
  chipBackgroundColor?: string;
  foregroundColor: string;
  mutedForegroundColor?: string; // Optional, defaults to main.mutedForegroundColor
  borderColor: string;
  borderWidth?: string; // Optional, defaults to "0px"
  shadow: string;
  textButton: TextButtonConfig;
  // AI Image block styling (optional overrides)
  aiImageBackgroundColor?: string;
  aiImageInsetShadow?: string;
}

/**
 * Configuration for a button layer (primary, secondary).
 * Each button layer has consistent styling properties for backgrounds, text, borders, and hover states.
 */
export interface ButtonLayerConfig {
  backgroundColor: string;
  foregroundColor: string;
  hoverBackgroundColor: string;
  hoverForegroundColor: string;
  shadow: string;
  borderColor: string;
}

/**
 * Configuration for the dashboard container layer.
 * This optional layer sits between the page background and the card content.
 * Most themes leave this undefined (transparent container).
 */
export interface DashboardContainerConfig {
  background: string; // e.g., '#FFFFFF' or 'rgba(45, 45, 45, 0.98)'
  borderColor?: string; // e.g., 'rgba(0, 0, 0, 0.1)' - defaults to 'transparent'
  borderRadius?: string; // e.g., '1.5rem' - defaults to general.borderRadius
  shadow?: string; // e.g., '0 4px 24px rgba(0, 0, 0, 0.15)'
}

export interface TailwindThemeConfig {
  // --------------------------------------------
  // Tailwind theme variables (see applyTheme.ts)
  // --------------------------------------------

  // General theme variables
  general: {
    fontFamily: string; // e.g., 'Inter, system-ui, sans-serif'
    borderRadius: string; // e.g., '0.5rem' for rounded-lg
    // Optional background for the entire dashboard page. If not set, defaults to main.backgroundColor
    pageBackground?: string; // e.g., 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)'
    // Background gradients palette (saturated/dark)
    cardBackgroundGradients: string[]; // Array of Tailwind gradient classes (e.g., 'from-blue-500 to-purple-600')
    // Used for AI image background color selection (dark themes get different default)
    isDarkTheme?: boolean;
  };

  // Text block styling — gradient background and optional border
  textBlock?: {
    background: string; // CSS background value (e.g., 'linear-gradient(116deg, #153AF0 43%, #528FFF 100%)')
    foregroundColor?: string; // Text color inside text block (default: #ffffff)
    border?: string; // Optional CSS border (e.g., '1px solid #FBC024' for Honey theme)
  };

  // Optional dashboard container layer (most themes leave this undefined)
  // When defined, creates a distinct container between page background and cards
  dashboardContainer?: DashboardContainerConfig;

  // UI Surfaces
  main: UISurfaceConfig; // Base surface (page background, primary text)
  nav: UISurfaceConfig; // Navigation bar
  card: UISurfaceConfig; // Cards (dashboard blocks, filters)

  // Button Layers
  primaryButton: ButtonLayerConfig;
  secondaryButton: ButtonLayerConfig;
}

export interface ChartJSThemeConfig {
  // Global defaults
  defaultColor: string; // Default text/label color
  defaultBackgroundColor: string;
  defaultBorderColor: string;

  // Color palette for datasets (12 colors for multi-series charts)
  colorPalette: [
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string
  ];

  // Semantic color tokens (optional; central DEFAULT_SEMANTIC_TOKENS fallback used when omitted).
  // Mode-aware green/red/gray that stay palette-INDEPENDENT so charts keep a stable
  // good/bad/neutral reading across palettes. Resolved via the `positive`/`negative`/`neutral`
  // color intents. `positive`/`negative`/`neutral` are the CANONICAL token names — the resolver
  // and agent docs reuse these exact strings; do not re-spell them.
  positive?: string;
  negative?: string;
  neutral?: string;

  // Grid styling
  gridColor: string;
  gridBorderColor: string;

  // Font settings
  fontFamily: string;
  fontSize: number;

  // Tooltip styling
  tooltipBackgroundColor: string;
  tooltipTextColor: string;
  tooltipBorderColor: string;

  // Legend styling
  legendTextColor: string;

  // Axis tick colors
  axisTickColor: string;

  // Line chart styling
  lineTension: number; // 0 = straight lines, 0.4 = smooth curves
  lineBorderWidth: number; // Line thickness

  // Point styling
  pointRadius: number; // Size of data points (0 = no points)
  pointHoverRadius: number; // Size when hovered
  pointBorderWidth: number; // Border thickness of points

  // Bar chart styling
  barBorderWidth: number; // Border thickness of bars (0 = no border/solid)
  barBorderRadius: number; // Corner radius of bars (0 = square)
  barBackgroundOpacity: number; // Background opacity for bar fills (0-1, 0.2 = semi-transparent, 0.9 = solid)

  // Line/area chart styling
  lineBackgroundOpacity?: number; // Background opacity for line chart fills (0-1, default 0.2)

  // Bar gradient options (optional) - legacy, prefer using plugins instead
  barGradient?: {
    enabled: boolean;
    // 'along-value-axis': gradient within each bar from base to tip
    // 'across-category-axis': gradient across bars from first to last category
    direction: "along-value-axis" | "across-category-axis";
    // Luminosity multiplier range: values < 1 darken, > 1 lighten, 1 = original
    startLuminosity?: number; // default 1.4 (lighter, at base or first category)
    endLuminosity?: number; // default 1.0 (original, at tip or last category)
  };

  // Optional Chart.js plugins for custom chart behavior
  // Each plugin can implement Chart.js lifecycle hooks (beforeInit, afterDatasetsDraw, etc.)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  plugins?: any[];
}

/**
 * Declarative color-intent vocabulary. The agent sets `dataset.__coeffColor` to one of
 * these strings; the resolver (createChart) maps the intent to a concrete color against the
 * live theme at render time.
 *
 * This union is the SINGLE canonical source of the intent strings — the resolver and the
 * agent-facing docs reuse these exact literals verbatim. Never re-spell them elsewhere.
 */
export type ColorIntent =
  | "auto" // resolver picks a sensible default for the chart shape
  | "single" // one deliberate accent color for a single-series chart
  | "shades" // monochrome shades derived from one hue
  | "rotate" // rotate through the full palette per dataset
  | "slot" // pin to a specific palette slot index
  | "firstN" // use the first N palette colors, then repeat
  | "emphasis" // highlight one series, mute the rest
  | "positive" // semantic good token (palette's chart-positive: its green/teal, or the primary)
  | "negative" // semantic bad token (palette's chart-negative: a muted grey on built-in palettes)
  | "neutral" // semantic neutral (gray) token
  | "fixed"; // an explicit fixed color supplied by the caller

/**
 * Central fallback for the mode-aware semantic tokens. Used when a `ChartJSThemeConfig`
 * omits `positive`/`negative`/`neutral`, so the ~19 existing theme literals don't all need
 * editing. Palette-INDEPENDENT green/red/gray; dark variants are brighter for contrast on
 * dark backgrounds.
 */
export const DEFAULT_SEMANTIC_TOKENS: {
  light: { positive: string; negative: string; neutral: string };
  dark: { positive: string; negative: string; neutral: string };
} = {
  light: {
    positive: "#16A34A", // green-600
    negative: "#DC2626", // red-600
    neutral: "#6B7280", // gray-500
  },
  dark: {
    positive: "#4ADE80", // green-400 (brighter for dark bg)
    negative: "#F87171", // red-400 (brighter for dark bg)
    neutral: "#9CA3AF", // gray-400 (brighter for dark bg)
  },
};

export interface AGGridThemeConfig {
  // Base theme to extend
  baseTheme: "material" | "quartz" | "balham";

  // Theme parameters (passed to withParams)
  params: Partial<AgGridGlobals.ThemeDefaultParams>;
}

/**
 * Compact, serializable definition of a custom/branded theme.
 *
 * Mirrors the backend `WebDashboardThemeSpec` dataclass in `coeff/server/spec.py` — keep the two in
 * sync. The AI/CLI produces just this small spec
 * (mode + 5 brand colors + background); `buildThemeFromSpec` deterministically expands it into a
 * full `Theme`. Custom themes are referenced by dashboards as `theme_id = "custom:{uuid}"` and the
 * resolved spec is embedded in the dashboard GET response.
 */
export interface ThemeSpec {
  name: string;
  mode: "light" | "dark";
  // Exactly 5 brand hex colors; colors[0] is the primary.
  colors: string[];
  background: {
    type: "solid" | "gradient";
    // Optional explicit page background colors for the light/dark variants (e.g. the brand's own
    // site background). When absent, both are derived from the 5 brand colors. Snake_case: this
    // mirrors the backend `WebDashboardThemeBackground` wire format verbatim.
    light_color?: string;
    dark_color?: string;
  };
  // Optional explicit semantic tokens. `positive`/`negative`/`neutral` are the TEXT tokens (KPI
  // delta text, nudged for contrast at render time); `chart_positive`/`chart_negative` are the
  // CHART fill tokens, used verbatim (the design pins a 40%-grey chart negative on every palette).
  // When omitted, `buildThemeFromSpec` GENERATES palette-proximate tokens from the 5 brand colors.
  // Built-in palettes pin their designer-picked values here; custom themes typically leave it
  // unset. Snake_case: mirrors the backend `WebDashboardThemeSemantics` wire format verbatim.
  semantics?: {
    positive?: string;
    negative?: string;
    neutral?: string;
    chart_positive?: string;
    chart_negative?: string;
  };
}
